import { toast } from "sonner";
import { FileDown, FileText, FileSpreadsheet } from "lucide-react";
import { API_BASE } from "@/lib/api";

const FORMATS = [
    { key: "pdf", label: "PDF", icon: FileDown },
    { key: "docx", label: "Word", icon: FileText },
    { key: "xlsx", label: "Excel", icon: FileSpreadsheet },
];

/**
 * Export a report to PDF / DOCX / XLSX.
 * Uses fetch (not axios) so we can download raw bytes and trigger a browser save.
 */
async function download(fmt, { title, content, confidentiality = "Executive" }) {
    const token = localStorage.getItem("sgsb.token");
    const resp = await fetch(`${API_BASE}/reports/export`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ title, content, fmt, confidentiality }),
    });
    if (!resp.ok) {
        throw new Error(`Export failed (${resp.status})`);
    }
    const cd = resp.headers.get("Content-Disposition") || "";
    const match = cd.match(/filename="?([^"]+)"?/i);
    const fallback = `SGSBateman_Report.${fmt}`;
    const filename = match ? match[1] : fallback;

    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 3000);
}

export default function ExportBar({ title, content, confidentiality = "Executive", testIdPrefix = "export" }) {
    const disabled = !content?.trim();

    const onExport = async (fmt) => {
        try {
            await download(fmt, { title, content, confidentiality });
            toast.success(`${fmt.toUpperCase()} downloaded`);
        } catch (e) {
            toast.error(`Export failed: ${e.message}`);
        }
    };

    return (
        <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="kicker text-slate-400 text-[10px] mr-2">Export</span>
            {FORMATS.map(({ key, label, icon: Icon }) => (
                <button
                    key={key}
                    data-testid={`${testIdPrefix}-${key}`}
                    disabled={disabled}
                    onClick={() => onExport(key)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--sgsb-gold)]/40 bg-slate-950/60 px-3 py-1.5 text-xs font-display font-bold text-[var(--sgsb-gold)] hover:bg-[var(--sgsb-gold)] hover:text-slate-950 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
                >
                    <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                    {label}
                </button>
            ))}
        </div>
    );
}
